import { MetricTabs } from '@openx/components-core/lib/MetricTabs/MetricTabs.tsx';
import { abbreviateNumericValue } from '@openx/utils/lib/abbreviateNumericValue.ts';
import { useCallback, useState } from 'react';
import type { Kpi, ReachKpis } from '../../data/chartTypes.ts';
import { useHouseholdInsightsData } from '../../data/hooks/useHouseholdInsightsData.ts';
import { parseAgeGender } from '../../data/parsers/parseAgeGender.ts';
import { parseHouseholdCtvs } from '../../data/parsers/parseHouseholdCtvs.ts';
import { parseHouseholdSize } from '../../data/parsers/parseHouseholdSize.ts';
import { parseReachKpis } from '../../data/parsers/parseReachKpis.ts';
import { InsightsBarChart } from '../InsightsBarChart/InsightsBarChart.tsx';
import { AgeGenderChart } from './components/AgeGenderChart.tsx';

type TabId = keyof ReachKpis;

const formatKpi = ({ value }: Kpi) => (value === null ? null : abbreviateNumericValue(value, { allowZero: true }));

export const GeneralData = () => {
  const { data: kpis, isLoading } = useHouseholdInsightsData(parseReachKpis);
  const { data: householdSize } = useHouseholdInsightsData(parseHouseholdSize);
  const { data: ageGender } = useHouseholdInsightsData(parseAgeGender);
  const { data: householdCtvs } = useHouseholdInsightsData(parseHouseholdCtvs);

  const [activeTab, setActiveTab] = useState<TabId>('households');
  const handleTabChange = useCallback((value: string) => setActiveTab(value as TabId), []);

  // `change` (period-over-period arrow) is intentionally not passed yet — `kpis[*].change` is ready for it
  const tabs = [
    {
      id: 'households',
      title: 'Households Reached',
      value: formatKpi(kpis.households),
      isLoading,
      content: <InsightsBarChart title="Household Size" data={householdSize} isLoading={isLoading} />,
    },
    {
      id: 'users',
      title: 'Reached Users',
      value: formatKpi(kpis.users),
      isLoading,
      content: <AgeGenderChart data={ageGender} isLoading={isLoading} />,
    },
    {
      id: 'ctvDevices',
      title: 'CTV Devices Reached',
      value: formatKpi(kpis.ctvDevices),
      isLoading,
      content: <InsightsBarChart title="Household - CTVs" data={householdCtvs} isLoading={isLoading} />,
    },
  ];

  return (
    <MetricTabs
      tabs={tabs}
      value={activeTab}
      onChange={handleTabChange}
      sx={{
        border: 0,
        borderRadius: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '& [role="tabpanel"]:not([hidden])': { flex: 1, minHeight: 0, p: 2.5 },
      }}
    />
  );
};
